import React, { createContext, useState, useEffect, useContext } from "react";
import { enforceQuotaAndListingLimits } from "../services/quotaService";
import { useAuth } from "./AuthProvider";

const QuotaContext = createContext(null);

/**
 * Provides user quota & KYC verification data globally.
 */
export const QuotaProvider = ({ children }) => {
  const { user } = useAuth();
  const [kycVerified, setKycVerified] = useState(false);
  const [canProceed, setCanProceed] = useState(false);
  const [usedFreeTrade, setUsedFreeTrade] = useState(false);
  const [freeTrades, setFreeTrades] = useState(0);
  const [tradesThisCycle, setTradesThisCycle] = useState(0);
  const [tradeLimit, setTradeLimit] = useState("N/A");
  const [activeListings, setActiveListings] = useState(0);
  const [listingLimit, setListingLimit] = useState("N/A");
  const [quotaExceeded, setQuotaExceeded] = useState(false);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkQuota = async () => {
      if (!user?.uid) return;
      setLoading(true);


      // Fetch user's trade quota details & active listings
      const { canProceed, kycVerified, usedFreeTrade, freeTrades, tradesThisCycle, tradeLimit, listingLimit, activeListings, quotaExceeded } =
        await enforceQuotaAndListingLimits(user.uid);

      // ✅ Set state values globally
      setCanProceed(canProceed || usedFreeTrade);
      setKycVerified(kycVerified);
      setUsedFreeTrade(usedFreeTrade);
      setFreeTrades(freeTrades ?? 0);
      setTradesThisCycle(tradesThisCycle ?? 0);
      setTradeLimit(tradeLimit ?? "N/A");
      setActiveListings(activeListings);
      setListingLimit(listingLimit ?? "N/A");
      setQuotaExceeded(quotaExceeded);

      // ✅ Calculate quotaExceeded
      if (
        (tradeLimit !== "N/A" && tradeLimit !== -1 && tradesThisCycle >= tradeLimit && freeTrades === 0) || // Trade limit check (not for unlimited plans)
        (freeTrades > 0 && activeListings >= 2) || // Free trade users can only have 1 active listing
        (freeTrades === 0 && listingLimit !== "N/A" && activeListings >= listingLimit) // Subscription users follow `listingLimit`
      ) {
        setQuotaExceeded(true);
      } else {
        setQuotaExceeded(false);
      }

      setLoading(false);
    };

    checkQuota();
  }, [user]);

  return (
    <QuotaContext.Provider
      value={{
        kycVerified,
        canProceed,
        usedFreeTrade,
        loading,
        freeTrades,
        tradesThisCycle,
        tradeLimit,
        activeListings,
        listingLimit,
        quotaExceeded,
      }}
    >
      {children}
    </QuotaContext.Provider>
  );
};

/**
 * Hook to use quota & KYC verification state.
 */
export const useQuota = () => {
  const context = useContext(QuotaContext);
  if (!context) {
    throw new Error("useQuota must be used within a QuotaProvider");
  }
  return context;
};

export default QuotaProvider;
